import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Compass, BarChart3, ShieldCheck, ArrowRight } from 'lucide-react';

/**
 * HabitaValueProposition — bloque de valor diferencial en Home.
 *
 * Sustituye temporalmente al PlatformStatsBar (números crudos del marketplace)
 * porque en esta etapa el volumen aún no refuerza confianza. Aquí comunicamos
 * los 4 diferenciales reales de Habita.
 *
 * Todos los ítems referencian capacidades YA existentes:
 *   - Habita Advisor (Advisor global)
 *   - Recomendaciones (Recommendation Engine + Explainability)
 *   - Comparaciones (Comparator sin ganador)
 *   - Verificación (marca de "inventario verificado")
 */
export default function HabitaValueProposition() {
  const items = [
    {
      icon: Compass,
      eyebrow: 'Con Habita Advisor',
      title: 'Un asesor que te acompaña',
      body: 'No solo busca propiedades — organiza todo tu proceso de decisión con evidencia.',
    },
    {
      icon: Sparkles,
      eyebrow: 'Recomendaciones personalizadas',
      title: 'Sugerencias con explicación',
      body: 'Cada propiedad que te mostramos viene con el porqué — nunca es un algoritmo opaco.',
    },
    {
      icon: BarChart3,
      eyebrow: 'Comparaciones inteligentes',
      title: 'Contrasta sin decidir por ti',
      body: 'Vemos fortalezas y matices de cada opción — el veredicto siempre queda de tu lado.',
    },
    {
      icon: ShieldCheck,
      eyebrow: 'Propiedades verificadas',
      title: 'Inventario que puedes confiar',
      body: 'Cada publicación pasa por controles de calidad antes de aparecer en Habita.',
    },
  ];

  return (
    <section
      className="max-w-7xl mx-auto px-4 lg:px-8 py-14 lg:py-20"
      data-testid="home-value-proposition"
    >
      <div className="max-w-2xl">
        <span className="text-xs uppercase tracking-widest text-habita font-semibold">
          Por qué Habita
        </span>
        <h2 className="font-serif text-3xl lg:text-5xl font-bold mt-2 text-habita-dark leading-tight">
          Una plataforma pensada para tu decisión
        </h2>
        <p className="mt-4 text-base lg:text-lg text-gray-600 max-w-xl">
          Cada elemento del producto está diseñado para acompañarte — no para venderte.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {items.map((it) => {
          const Icon = it.icon;
          const testKey = it.eyebrow.split(' ')[1].toLowerCase();
          return (
            <div
              key={it.title}
              className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md hover:border-habita/30 transition-all"
              data-testid={`value-${testKey}`}
            >
              <div className="w-11 h-11 rounded-full bg-habita text-white grid place-items-center mb-4">
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-[11px] uppercase tracking-widest text-habita/80 font-semibold mb-1">
                {it.eyebrow}
              </p>
              <p className="font-serif text-xl font-bold text-habita-dark leading-snug mb-2">
                {it.title}
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                {it.body}
              </p>
            </div>
          );
        })}
      </div>

      <div className="mt-10 flex items-center gap-3 flex-wrap">
        <Link
          to="/advisor/inicio"
          data-testid="value-cta-explore"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-habita text-white text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          Comenzar con Habita Advisor
          <ArrowRight className="w-4 h-4" />
        </Link>
        <Link
          to="/mi-decision"
          data-testid="value-cta-workspace"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-habita/30 text-habita-dark text-sm font-semibold hover:bg-habita-mint transition-colors"
        >
          Conocer Mi decisión
        </Link>
      </div>
    </section>
  );
}
